import { LightningIcon, ResourceTypeIcon } from "@theprelude/ds";
import { ControlCategories } from "@theprelude/sdk";
import { ReactNode } from "react";
import type { ExecutiveSummaryTab } from "@/routes/_app.executive-summary";
import { CategoryControlsCard } from "./cards/category-controls-card";
import { CategoryControlsMisconfigurationsCard } from "./cards/category-controls-misconfigurations-card";
import { CategoryCriticalMisconfigurationsCard } from "./cards/category-critical-misconfigurations-card";
import { ControlPoliciesCard } from "./cards/control-policies-card";
import { DeviceMissingScanCard } from "./cards/device-missing-scan-card";
import { DeviceOutOfDateScanCard } from "./cards/device-out-of-date-scan-card";
import { DeviceRFMCard } from "./cards/device-rfm-card";
import { ExtendedMFAChartCard } from "./cards/extended-mfa-chart-card";
import { ExtendedRFMChartCard } from "./cards/extended-rfm-chart-card";
import { MissingEDRCard } from "./cards/missing-edr-card";
import { MissingEndpointManagementCard } from "./cards/missing-endpoint-management-card";
import { MissingMFACard } from "./cards/missing-mfa-card";
import { MissingVulnerabilityManagementCard } from "./cards/missing-vulnerability-management-card";
import { RecentThreatsCard } from "./cards/recent-threats-card";
import { ColumnHeader, ErrorBoundaryCard } from "./sections";

export const useExecutiveSummarySections = ({
  activeTabs,
}: {
  activeTabs: readonly ExecutiveSummaryTab[];
}) => {
  const sections: ReactNode[] = [];

  if (activeTabs.includes("endpoint")) {
    sections.push(
      <div key="endpoint-control-failures">
        <ColumnHeader icon={<ResourceTypeIcon resourceType="endpoint" />}>
          Endpoint Control Failures
        </ColumnHeader>
        <ErrorBoundaryCard>
          <MissingEDRCard />
        </ErrorBoundaryCard>
        <ErrorBoundaryCard>
          <MissingEndpointManagementCard />
        </ErrorBoundaryCard>
        <ErrorBoundaryCard>
          <MissingVulnerabilityManagementCard />
        </ErrorBoundaryCard>
        <ErrorBoundaryCard>
          <DeviceMissingScanCard />
        </ErrorBoundaryCard>
        <ErrorBoundaryCard>
          <DeviceOutOfDateScanCard />
        </ErrorBoundaryCard>
        <ErrorBoundaryCard>
          <DeviceRFMCard />
        </ErrorBoundaryCard>
        <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
          <ErrorBoundaryCard>
            <ExtendedRFMChartCard days={7} />
          </ErrorBoundaryCard>
          <ErrorBoundaryCard>
            <ExtendedRFMChartCard days={3} />
          </ErrorBoundaryCard>
        </div>
      </div>,
      <div key="endpoint-control-configuration">
        <ColumnHeader icon={<ResourceTypeIcon resourceType="endpoint" />}>
          Endpoint Control Configuration
        </ColumnHeader>
        <ErrorBoundaryCard>
          <CategoryControlsCard category={ControlCategories.XDR} />
        </ErrorBoundaryCard>
        <ErrorBoundaryCard>
          <ControlPoliciesCard category={ControlCategories.XDR} />
        </ErrorBoundaryCard>
        <ErrorBoundaryCard>
          <CategoryCriticalMisconfigurationsCard
            category={ControlCategories.XDR}
          />
        </ErrorBoundaryCard>
      </div>,
    );
  }

  if (activeTabs.includes("user")) {
    sections.push(
      <div key="user-control-failures">
        <ColumnHeader icon={<ResourceTypeIcon resourceType="user" />}>
          User Control Failures
        </ColumnHeader>
        <ErrorBoundaryCard>
          <MissingMFACard />
        </ErrorBoundaryCard>
        <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
          <ErrorBoundaryCard>
            <ExtendedMFAChartCard days={7} />
          </ErrorBoundaryCard>
          <ErrorBoundaryCard>
            <ExtendedMFAChartCard days={3} />
          </ErrorBoundaryCard>
        </div>
      </div>,
      <div key="user-control-configuration">
        <ColumnHeader icon={<ResourceTypeIcon resourceType="user" />}>
          User Control Configuration
        </ColumnHeader>
        <ErrorBoundaryCard>
          <CategoryControlsMisconfigurationsCard
            category={ControlCategories.IDENTITY}
            description="Total misconfigurations across all identity providers."
          />
        </ErrorBoundaryCard>
      </div>,
    );
  }

  if (activeTabs.includes("email")) {
    sections.push(
      <div key="email-control-configuration">
        <ColumnHeader icon={<ResourceTypeIcon resourceType="inbox" />}>
          Email Control Configuration
        </ColumnHeader>
        <ErrorBoundaryCard>
          <CategoryControlsMisconfigurationsCard
            category={ControlCategories.EMAIL}
            description="Total misconfigurations across all email controls."
          />
        </ErrorBoundaryCard>
        <ErrorBoundaryCard>
          <ControlPoliciesCard category={ControlCategories.EMAIL} />
        </ErrorBoundaryCard>
      </div>,
    );
  }

  sections.push(
    <div key="threat-profile">
      <ColumnHeader icon={<LightningIcon />}>Threat Profile</ColumnHeader>
      <ErrorBoundaryCard>
        <RecentThreatsCard />
      </ErrorBoundaryCard>
    </div>,
  );

  return sections;
};
